import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Space,
  Popconfirm,
  Breadcrumb,
  Steps,
} from 'antd';
import {
  PlusOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  FilterOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
const { Title, Text } = Typography;
const { Option } = Select;

const PurchaseOrder = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    const mockSuppliers = [
      { id: 1, name: 'Global Auto Parts Ltd.', contact: 'Mr. Lee', country: 'China' },
      { id: 2, name: 'Euro Spares Inc.', contact: 'Ms. Schmidt', country: 'Germany' },
      { id: 3, name: 'American Machinery Co.', contact: 'Mr. Smith', country: 'USA' },
    ];
    setSuppliers(mockSuppliers);
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [poRes] = await Promise.all([
        api.get('/api/purchase-orders'),
      ]);
      setPurchaseOrders(poRes.data || []);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePO = async (id) => {
    try {
      await api.delete(`/api/purchase-orders/${id}`);
      messageApi.success('Purchase Order deleted');
      fetchData();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const exportPOToExcel = (po) => {
    const supplier = suppliers.find((s) => s.id === po.supplierId) || {};
    const headerData = [
      ['PURCHASE ORDER'],
      [],
      ['PO Number', po.purchaseOrderId || po.id],
      ['Date', po.orderDate ? new Date(po.orderDate).toLocaleDateString() : ''],
      ['Status', po.status || 'Pending'],
      ['Supplier', supplier.name || 'N/A'],
      ['Currency', po.currency || 'USD'],
      ['Shipping Cost', po.shippingCost || 0],
      ['Shipping Terms', po.shippingTerms || ''],
      ['Notes', po.description || ''],
      [],
      ['Importer ID', 'Part Name', 'Part Number', 'Ordered Qty', 'Received Qty', 'UOM', 'Unit Cost', 'Line Total'],
    ];
    const itemsData = (po.items || []).map((item) => [
      item.sparepart?.importerId || '',
      item.sparepart?.partName || '',
      item.sparepart?.partNumber || '',
      item.quantity || 0,
      item.receivedQty || 0,
      item.sparepart?.measureUnit || 'pcs',
      item.unitCost || 0,
      ((item.quantity || 0) * (item.unitCost || 0)).toFixed(2),
    ]);
    const totalRow = [
      ['', '', '', '', '', '', 'Subtotal', (po.totalCost || 0).toFixed(2)],
      ['', '', '', '', '', '', 'Shipping', (po.shippingCost || 0).toFixed(2)],
      ['', '', '', '', '', '', 'Grand Total', ((po.totalCost || 0) + (po.shippingCost || 0)).toFixed(2)],
    ];
    const finalData = [...headerData, ...itemsData, ...totalRow];
    const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 30 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Order');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${po.purchaseOrderId || 'PO'}.xlsx`);
  };

  const exportAllPOsToExcel = () => {
    const data = purchaseOrders.map((po) => {
      const supplier = suppliers.find((s) => s.id === po.supplierId);
      return {
        'PO Number': po.purchaseOrderId || po.id,
        Supplier: supplier?.name || 'N/A',
        'Order Date': po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '',
        Status: po.status || 'Pending',
        Currency: po.currency || 'USD',
        'Total Cost': po.totalCost || 0,
        'Shipping Cost': po.shippingCost || 0,
        'Grand Total': (po.totalCost || 0) + (po.shippingCost || 0),
        'Shipping Terms': po.shippingTerms || '',
        Notes: po.description || '',
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Purchase Orders');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `All_Purchase_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToPDF = (po) => {
    const doc = new jsPDF();
    const supplier = suppliers.find((s) => s.id === po.supplierId) || {};
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('JS CONSTRUCTION MACHINERY', 105, 18, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('123 Industrial Way, Your City, 12345', 105, 25, { align: 'center' });
    doc.text('Tel: +1-234-567-8900  |  Email: purchase@jsconstruction.com', 105, 30, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 34, 196, 34);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PURCHASE ORDER', 105, 44, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`PO Number: ${po.purchaseOrderId || po.id}`, 14, 55);
    doc.text(`Date: ${new Date(po.orderDate || po.createdAt).toLocaleDateString()}`, 14, 62);
    doc.text(`Status: ${po.status || 'Pending'}`, 14, 69);
    autoTable(doc, {
      startY: 75,
      head: [['Supplier Information']],
      body: [[`${supplier.name || 'N/A'}\nContact: ${supplier.contact || 'N/A'}\nCountry: ${supplier.country || 'N/A'}`]],
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
    });
    const tableData = (po.items || []).map((item) => [
      item.sparepart?.importerId || item.importerId || '—',
      item.sparepart?.partName || item.partName || '—',
      item.sparepart?.partNumber || item.partNumber || '—',
      item.quantity,
      item.sparepart?.measureUnit || item.measureUnit || 'pcs',
      `$${(item.unitCost || 0).toFixed(2)}`,
      `$${((item.quantity || 0) * (item.unitCost || 0)).toFixed(2)}`,
    ]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [['Importer ID', 'Part Name', 'Part Number', 'Qty', 'UOM', 'Unit Cost', 'Line Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [22, 160, 133], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 45 }, 2: { cellWidth: 30 }, 3: { cellWidth: 15 }, 4: { cellWidth: 15 }, 5: { cellWidth: 22 }, 6: { cellWidth: 25 } },
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    const shipping = po.shippingCost || 0;
    const grand = (po.totalCost || 0) + shipping;
    doc.setFontSize(11);
    doc.text(`Subtotal: $${(po.totalCost || 0).toFixed(2)}`, 140, finalY);
    doc.text(`Shipping: $${shipping.toFixed(2)}`, 140, finalY + 7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`GRAND TOTAL: $${grand.toFixed(2)}`, 140, finalY + 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Shipping Terms: ${po.shippingTerms || 'N/A'}`, 14, finalY + 10);
    doc.text(`Notes: ${po.description || 'N/A'}`, 14, finalY + 17);
    const pageHeight = doc.internal.pageSize.height;
    doc.setLineWidth(0.3);
    doc.line(14, pageHeight - 25, 196, pageHeight - 25);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('JS Construction Machinery – Authorized Purchase Order', 105, pageHeight - 18, { align: 'center' });
    doc.text('This document is computer generated and does not require a signature.', 105, pageHeight - 13, { align: 'center' });
    doc.save(`${po.purchaseOrderId || 'PO'}.pdf`);
  };

  const mainColumns = [
    {
      title: 'PO ID',
      dataIndex: 'purchaseOrderId',
      key: 'purchaseOrderId',
      render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierId',
      key: 'supplier',
      responsive: ['md'],
      render: (id) => {
        const supplier = suppliers.find((s) => s.id === id);
        return supplier ? (
          <Space direction="vertical" size={0}>
            <Text strong>{supplier.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{supplier.country}</Text>
          </Space>
        ) : 'N/A';
      },
    },
    {
      title: 'Order Date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      title: 'Items',
      key: 'itemCount',
      render: (_, record) => <Tag color="blue">{record.items?.length || 0} items</Tag>,
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      responsive: ['sm'],
      render: (cost) => <Text strong style={{ color: '#52c41a' }}>${(cost || 0).toFixed(2)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      responsive: ['md'],
      render: (status) => (
        <Tag color={status === 'Completed' ? 'green' : status === 'Cancelled' ? 'red' : 'orange'}>
          {status || 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/admin/purchase-order/view/${record.id}`)}>
            View
          </Button>
          <Button icon={<FilePdfOutlined />} size="small" type="primary" onClick={() => exportToPDF(record)}>
            PDF
          </Button>
          <Button icon={<FileExcelOutlined />} size="small" onClick={() => exportPOToExcel(record)}>
            Excel
          </Button>
          <Popconfirm title="Delete this Purchase Order?" onConfirm={() => handleDeletePO(record.id)} okText="Yes" cancelText="No">
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, minHeight: '100vh', }}>
      {contextHolder}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Admin</Breadcrumb.Item>
        <Breadcrumb.Item>Purchase Order</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <ShoppingCartOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Purchase Order Management
                </Title>
                <Text type="secondary">
                  Manage and track all your purchase orders
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<FilterOutlined />} onClick={() => navigate('/admin/purchase-order/bulk-create')}>
                Bulk Create PO
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/purchase-order/create')}>
                Create New PO
              </Button>
            </Space>
          </Col>
        </Row>

        <Card
          size="small"
          style={{ marginBottom: 16, background: '#fafafa' }}
          extra={
            <Button size="small" onClick={exportAllPOsToExcel}>
              Export All to Excel
            </Button>
          }
        >
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Total Purchase Orders:</Text>
            </Col>
            <Col>
              <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>{purchaseOrders.length}</Tag>
            </Col>
            <Col>
              <Text type="secondary">
                Total Value: <Text strong style={{ color: '#52c41a' }}>${purchaseOrders.reduce((sum, po) => sum + (po.totalCost || 0), 0).toFixed(2)}</Text>
              </Text>
            </Col>
          </Row>
        </Card>

        <Table
          columns={mainColumns}
          dataSource={purchaseOrders}
          rowKey={(r) => r.id || r.purchaseOrderId}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} purchase orders` }}
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default PurchaseOrder;
